import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import KLink from "@kikoff/components/src/v1/navigation/KLink";
import useErrorConsumer from "@kikoff/hooks/src/useErrorConsumer";
import { cls } from "@kikoff/utils/src/string";

import PageAlign from "@component/dashboard/offer/page_align";
import PhoneDuringOnboardingSection from "@component/dashboard/store/phone_during_onboarding_section";
import { PageRegistration } from "@component/flows/hooks/usePageFlow";
import StreamlinedHeader from "@component/onboarding/streamlined_header";
import { FREE_BASIC_DISCOUNTED_PREMIUM } from "@constant/promos";
import { initCreditV2, selectCredit } from "@feature/credit";
import { selectCreditAccount } from "@feature/credit_line";
import { selectShowPhoneSupport } from "@feature/page";
import {
  fetchCheckoutPreview,
  selectProductForPlan,
  updateBag,
  updateCatalog,
} from "@feature/shopping";
import { Plan } from "@feature/subscription";
import { selectPromo } from "@feature/user";
import { track } from "@util/analytics";
import { l10nResources } from "@util/l10n";

import { MoneyBackGuarantee, StoreDisclaimer } from "../select_plan";

import SelectPlanSection from "./select_plan_section";

import styles from "./styles.module.scss";

const l10n = l10nResources({
  en: `
start-building-credit = Start your credit building journey
start-building-description = Select one of our 12-month plans to get started. There are no credit checks or hidden fees.
basic = Basic
premium = Premium
ultimate = Ultimate
disputes-disclaimer = If you want to send a dispute letter without signing up for a plan, email your latest credit report to
disputes-disclaimer-end = and we will get back to you.
month-abbr = mo
continue = Continue
`,
  es: `
start-building-credit = Comience su viaje de construcción de crédito
start-building-description = Seleccione uno de nuestros planes de 12 meses para comenzar. No hay verificaciones de crédito ni tarifas ocultas.
basic = Básica
premium = Premium
ultimate = Ultimate
disputes-disclaimer = Si desea enviar una carta de disputa sin inscribirse en un plan, envíe por correo electrónico su último informe crediticio a
disputes-disclaimer-end = y nos comunicaremos con usted.
month-abbr = mes
continue = Continuar
`,
});

interface UltimateSelectPlanViewProps extends PageRegistration {
  showBack?: boolean;
  showMoneyBackGuarantee?: boolean;
  plans?: Plan[];
}

function UltimateSelectPlan({
  configFlow,
  page,
  showBack = false,
  showMoneyBackGuarantee = false,
  plans = ["ultimate", "premium", "basic"],
  Button,
}: UltimateSelectPlanViewProps) {
  const dispatch = useDispatch();
  const error = useErrorConsumer();

  const productByPlan = {
    ultimate: useSelector(selectProductForPlan("ULTIMATE")),
    premium: useSelector(selectProductForPlan("PREMIUM")),
    basic: useSelector(selectProductForPlan("BASIC")),
  };
  const preview = useSelector((state) => state.shopping.checkoutPreview);
  const showPhoneSupport = useSelector(selectShowPhoneSupport());

  const notLoaded = !productByPlan.basic;

  const [selectedPlan, setSelectedPlan] = useState<Plan>(plans[0]);

  const selectedProduct = productByPlan[selectedPlan];

  useEffect(() => {
    if (notLoaded) dispatch(updateCatalog());
  }, []);

  useEffect(() => {
    if (preview && selectedProduct) {
      dispatch(
        updateBag({
          items: [{ product: selectedProduct, quantity: 1 }],
          autoRenew: {
            status: preview.autoRenew?.status,
          },
        })
      );
    }
  }, [preview, selectedProduct]);

  useEffect(() => {
    if (selectedProduct) {
      dispatch(
        fetchCheckoutPreview(selectedProduct.token, { isRenewal: false })
      ).catch(error.throw);
    }
  }, [selectedProduct]);

  const userAuthenticated = useSelector((state) => state.user.authenticated);
  const creditLine = useSelector(selectCreditAccount());
  const reactivating = creditLine?.closedAt;
  const credit =
    process.env.NODE_ENV === "production"
      ? useSelector(selectCredit())
      : { currentCreditScore: { score: 350 } };

  useEffect(() => {
    if (!credit && userAuthenticated) dispatch(initCreditV2());
    if (reactivating) track.impression("CA - Activation - Restart Screen");
    track.impression("CA - Ultimate Select Plan - Ultimate Education View");
  }, []);

  configFlow
    .button(
      <Button className={styles["continue-button"]}>
        {l10n.getString("continue")}
      </Button>
    )
    .bottom(
      showPhoneSupport && (
        <div className="mt-1 column-center">
          <PhoneDuringOnboardingSection />
        </div>
      ),
      [showPhoneSupport]
    );

  return (
    <PageAlign scroll overscroll="200px" maxWidth={550}>
      <StreamlinedHeader
        displayBackButton={showBack}
        displayCloseButton
        page={page}
      />
      <div className="mx-3 mt-4">
        <div className="text:heading-4 mb-3">
          {l10n.getString("start-building-credit")}
        </div>
        <div>{l10n.getString("start-building-description")}</div>
        <SelectPlanSection
          plansToShow={plans}
          selectedPlanKey={selectedPlan}
          setSelectedPlanKey={setSelectedPlan}
        />
        <FooterText showMoneyBackGuarantee={showMoneyBackGuarantee} />
      </div>
    </PageAlign>
  );
}

interface FooterTextProps {
  showMoneyBackGuarantee?: boolean;
}
function FooterText({ showMoneyBackGuarantee }: FooterTextProps) {
  const showFreePromo = useSelector(selectPromo(FREE_BASIC_DISCOUNTED_PREMIUM));

  return (
    <div className={styles["select-product-footer"]}>
      <div className={cls("text:mini", styles["store-disclaimer"])}>
        <sup className="color:moderate mt-1 mr-0.5">1</sup> <StoreDisclaimer />
      </div>
      <div className="text:mini">
        <sup>2</sup> {l10n.getString("disputes-disclaimer")}{" "}
        <KLink
          variant="underlined"
          href="mailto:disputecreditreportupload@kikoff.com"
        >
          disputecreditreportupload@kikoff.com
        </KLink>
        , {l10n.getString("disputes-disclaimer-end")}
      </div>
      {showFreePromo && (
        <div className="text:mini">*{l10n.getString("promo-disclaimer")}</div>
      )}
      {showMoneyBackGuarantee && (
        <div className="mt-4">
          <MoneyBackGuarantee />
        </div>
      )}
    </div>
  );
}

export default UltimateSelectPlan;
